import React from 'react';
import {getComponentConfig, loadComponent, loadComponentById} from '../../utils/components';
import ContentLoader from "../layout/ContentLoader";
import PropTypes from "prop-types";
import {store} from "src/store";
import {showModal} from "../../actions";
import {trace} from "../../utils/request";
import Icon from "./Icon";
import {set} from "../../utils/dotutils";
import PropsProxy from "../layout/PropsProxy";
import axios from "axios";
import debounce from "../../utils/debounce";

type ComponentLoaderPropsType = ({
    componentName: string,
}|{
    componentName: "componentlist",
    component: string,
    itemsperpage?: number,
})&{
    componentId?: string|number,
    optionname?: string,
    filter?: any,
    aggregations?: any,
    order?: any,
    view?: any,
    componentAs?: string|((component: any, props: ComponentLoaderPropsType) => any),
    onLoadCompleted?: (component: any) => void,
    onBeforeLoad?: () => void,
    onFailure?: boolean|((reason: any) => void),
    dynamic?: boolean,
    loader?: any,
}

type ComponentLoaderStateType = {
    isLoading: boolean,
    failed: boolean,
    component: any
}

export default class ComponentLoader extends React.Component<ComponentLoaderPropsType, ComponentLoaderStateType> {

    state: ComponentLoaderStateType = {
        isLoading: true,
        failed: false,
        component: null
    }

    public loadComponentDebounce: any;
    public loadComponentRequest: any;

    constructor(props: ComponentLoaderPropsType) {
        super(props);
        this.loadComponentDebounce = debounce(() => {
            return new Promise(async (resolve, reject) => {
                if (this.props.onBeforeLoad) {
                    await this.props.onBeforeLoad();
                }
                this.loadComponentRequest && this.loadComponentRequest.abort();
                if (this.props.componentId) {
                    this.loadComponentRequest = loadComponentById(this.props.componentName, this.props.componentId, this.props.view);
                } else {
                    const params: any = {
                        filter: this.props.filter,
                        group: this.props.aggregations,
                        order: this.props.order
                    };
                    if (this.props.componentName === "componentlist") {
                        params.component = (this.props as any).component;
                        params.itemsperpage = (this.props as any).itemsperpage;
                    }
                    if (this.props.optionname) {
                        params.optionname = this.props.optionname;
                    }
                    this.loadComponentRequest = loadComponent(this.props.componentName, params, this.props.view);
                }
                this.loadComponentRequest.then(async (component: any) => {
                    if (this.props.onLoadCompleted) {
                        await this.props.onLoadCompleted(component);
                    }
                    resolve(component);
                    this.setState({isLoading: false, component, failed: false});
                });
                this.loadComponentRequest.catch((e: any) => {
                    if (!(e instanceof axios.Cancel)) {
                        reject(e);
                        console.error(e);
                        this.setState({failed: e, isLoading: false, component: null});
                        !this.props.onFailure && store.dispatch(showModal({
                            size: "large",
                            theme: "danger",
                            header: "Szerveroldali hiba",
                            body: trace({error: e})
                        }));
                    }
                });
            });
        }, 300);
        this.loadComponentDebounce();
    }

    loadComponent = () => {
        this.setState({isLoading: true, failed: false, component: null});
        return this.loadComponentDebounce();
    }

    refresh = () => this.loadComponent();

    componentDidUpdate(prevProps: ComponentLoaderPropsType, prevState: ComponentLoaderStateType) {
        if(
            prevProps.componentName !== this.props.componentName
            ||
            prevProps.componentId !== this.props.componentId
            ||
            JSON.stringify(prevProps.filter) !== JSON.stringify(this.props.filter)
            ||
            prevProps.optionname !== this.props.optionname
        ) {
            this.loadComponent();
        }
    }

    render () {
        let component: any = null;
        let props: Record<string, any> = {};
        if (!this.state.isLoading && !this.state.failed) {
            const componentAs = (this.props.componentAs || "values");
            if (this.props.dynamic || !(componentAs instanceof Function)) {
                set(props, this.props.dynamic ? this.props.componentName : componentAs, this.state.component);
            } else if (componentAs instanceof Function) {
                props = {
                    ...componentAs(this.state.component, {...this.props})
                };
            }
            component = <PropsProxy {...props}>
                {this.props.children}
            </PropsProxy>;
        } else if(this.state.failed) {
            if (this.props.onFailure) {
                component = this.props.onFailure instanceof Function ? this.props.onFailure(this.state.failed) : this.props.onFailure;
            }
        } else if (this.props.loader === true) {
            component = this.props.children;
        }
        if (this.state.isLoading) {
            let loaderStyle;
            if (this.props.loader !== undefined) {
                loaderStyle = this.props.loader;
                if (loaderStyle instanceof Function) {
                    loaderStyle = loaderStyle({children: component});
                } else if(loaderStyle !== false) {
                    loaderStyle = <loaderStyle.type {...loaderStyle.props}>
                        {component}
                    </loaderStyle.type>;
                }
            }
            if (loaderStyle) {
                return loaderStyle;
            } else {
                if (loaderStyle === false) {
                    return <PropsProxy {...props}>{this.props.children}</PropsProxy>;
                }
                return React.Children.map(this.props.children, (child: any) => {
                    if (child && typeof child === "object" && child?.type?.placeHolder) {
                        return <child.type.placeHolder
                            {...child.props}
                        />;
                    } else {
                        return <ContentLoader {...props}>
                            {component}
                        </ContentLoader>;
                    }
                });
            }
        } else {
            return component || <Icon lib="fa" name="exclamation-triangle" />;
        }
    }
}
